<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import * as THREE from 'three';
  import { ThreeUtils } from '../lib/threeUtils';
  import { CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
  import type { VideoData, KeywordData } from '../types';
  import { visualizationActions } from '../stores/visualization';
  
  export let videos: VideoData[] = [];
  export let keywords: KeywordData[] = [];
  export let viewType: 'sphere' | 'universe' | 'pearl' = 'sphere';
  
  let container: HTMLElement;
  let scene: THREE.Scene;
  let camera: THREE.PerspectiveCamera;
  let renderer: THREE.WebGLRenderer;
  let css3dRenderer: any;
  let controls: any;
  let animationId: number;
  let originalCameraPosition: THREE.Vector3;
  let originalTarget: THREE.Vector3 | null = null;
  const _tmpVec3 = new THREE.Vector3();
  const _worldPos = new THREE.Vector3();
  const _worldDir = new THREE.Vector3();
  
  onMount(() => {
    initializeScene();
    setupEventListeners();
  });
  
  onDestroy(() => {
    cleanup();
  });
  
  function initializeScene() {
    // Create scene and camera
    scene = ThreeUtils.createScene();
    camera = ThreeUtils.createCamera(container.clientWidth, container.clientHeight);
    
    // Create renderers
    renderer = ThreeUtils.createWebGLRenderer(container);
    css3dRenderer = ThreeUtils.createCSS3DRenderer(container);
    
    // Add renderers to container
    container.appendChild(renderer.domElement);
    container.appendChild(css3dRenderer.domElement);
    
    // Create controls
    controls = ThreeUtils.createTrackballControls(camera, css3dRenderer.domElement);
    // Look at scene center to stabilize initial orientation and clickability
    if (controls && controls.target) {
      controls.target.set(0, 0, 0);
      controls.update();
    }
    // Ensure CSS3D captures interactions; some controls capture pointerdown
    if (css3dRenderer?.domElement) {
      css3dRenderer.domElement.style.pointerEvents = 'auto';
    }
    originalCameraPosition = camera.position.clone();
    if (controls && controls.target) {
      originalTarget = controls.target.clone();
    }
    
    // Start animation loop
    animate();
    
    // Setup responsive behavior
    ThreeUtils.setupWindowResize(camera, renderer, css3dRenderer);
  }
  
  function setupEventListeners() {
    // Camera focus on video play
    if (container) {
      container.addEventListener('video-play', handleVideoPlay as EventListener);
      container.addEventListener('video-ended', handleVideoEnded as EventListener);
    }
  }
  
  function animate() {
    animationId = requestAnimationFrame(animate);
    controls.update();
    
    // Keep CSS3D objects facing the camera (billboarding). Align up with camera to avoid upside-down flips.
    if (scene && camera) {
      scene.traverse((obj) => {
        if (obj instanceof CSS3DObject) {
          // Horizontal billboarding: compute yaw only to avoid vertical flips
          _tmpVec3.subVectors(camera.position, obj.position);
          const yaw = Math.atan2(_tmpVec3.x, _tmpVec3.z);
          obj.rotation.set(0, yaw, 0);

          // Depth-based z-index to ensure correct hit-testing order
          obj.getWorldPosition(_worldPos);
          const distance = (camera as unknown as THREE.PerspectiveCamera).position.distanceTo(_worldPos);
          const zIndex = Math.max(0, 100000 - Math.floor(distance));
          (obj.element as HTMLElement).style.zIndex = String(zIndex);

          // Facing check to hint when not interactable from current angle
          obj.getWorldDirection(_worldDir); // object's forward (-Z)
          const toCam = _tmpVec3.copy((camera as unknown as THREE.PerspectiveCamera).position).sub(_worldPos).normalize();
          const facingDot = _worldDir.dot(toCam);
          const isFacingCamera = facingDot > 0.05; // small threshold
          const el = obj.element as HTMLElement;
          const hint = el.querySelector('.flip-hint') as HTMLElement | null;
          if (isFacingCamera) {
            el.style.opacity = '1';
            el.style.cursor = 'pointer';
            if (hint) hint.style.display = 'none';
          } else {
            el.style.opacity = '0.6';
            el.style.cursor = 'not-allowed';
            if (hint) hint.style.display = 'block';
          }
        }
      });
    }

    // Render both scenes; ensure CSS3D is above WebGL
    renderer.render(scene, camera);
    css3dRenderer.render(scene, camera);
  }
  
  function cleanup() {
    if (animationId) {
      cancelAnimationFrame(animationId);
    }
    
    if (scene) {
      ThreeUtils.disposeScene(scene);
    }
    
    if (renderer) {
      renderer.dispose();
    }
    
    if (controls) {
      controls.dispose();
    }
    if (container) {
      container.removeEventListener('video-play', handleVideoPlay as EventListener);
      container.removeEventListener('video-ended', handleVideoEnded as EventListener);
    }
  }
  
  // Reactive updates when data changes
  $: if (scene && (videos.length > 0 || keywords.length > 0)) {
    updateScene();
  }
  
  function updateScene() {
    // Clear existing objects
    while(scene.children.length > 0) {
      scene.remove(scene.children[0]);
    }
    
    if (viewType === 'sphere') {
      renderKeywordSphere();
    } else {
      renderVideoSpace();
    }
  }
  
  function renderKeywordSphere() {
    keywords.forEach(keywordData => {
      const element = ThreeUtils.createKeywordElement(keywordData);
      element.addEventListener('keyword-click', (e: Event) => {
        const detail = (e as CustomEvent).detail;
        if (detail?.keyword) {
          visualizationActions.toggleKeywordFilter(detail.keyword);
        }
      });
      const object = new CSS3DObject(element);
      
      object.position.set(
        keywordData.position.x,
        keywordData.position.y,
        keywordData.position.z
      );
      
      scene.add(object);
    });
  }
  
  function renderVideoSpace() {
    videos.forEach(videoData => {
      const element = ThreeUtils.createVideoElement(videoData);
      const object = new CSS3DObject(element);
      
      object.position.set(
        videoData.position.x,
        videoData.position.y,
        videoData.position.z
      );

      // Ensure a consistent upright orientation initially
      object.rotation.set(0, 0, 0);
      
      scene.add(object);
    });
  }

  function handleVideoPlay(e: CustomEvent) {
    const id = (e as any).detail?.id as string | undefined;
    if (!id) return;
    const video = videos.find(v => v.id === id);
    if (!video) return;
    flyToVideo(video);
  }

  function handleVideoEnded(e: CustomEvent) {
    flyBack();
  }

  function flyToVideo(video: VideoData) {
    const target = new THREE.Vector3(video.position.x, video.position.y, video.position.z);
    const fromPos = camera.position.clone();
    // Approach from current direction to avoid sudden flips
    let approachDir = fromPos.clone().sub(target);
    if (approachDir.length() < 1e-3) {
      approachDir = new THREE.Vector3(0, 0, 1); // fallback
    }
    approachDir.normalize();

    // Distance scaled by video size, clamped to a friendly range
    const baseSize = Math.max(video.size?.width ?? 240, video.size?.height ?? 160);
    const desiredDistance = Math.max(500, Math.min(1200, baseSize * 3));

    // Add a small lateral and upward offset for a nicer composition
    const up = new THREE.Vector3(0, 1, 0);
    const side = new THREE.Vector3().crossVectors(approachDir, up).normalize();
    const upOffset = up.clone().multiplyScalar(80);
    const sideOffset = side.clone().multiplyScalar(40);

    const toPos = target
      .clone()
      .add(approachDir.clone().multiplyScalar(desiredDistance))
      .add(upOffset)
      .add(sideOffset);

    const fromTarget = controls?.target?.clone() ?? new THREE.Vector3();
    const toTarget = target.clone();
    animateCamera(fromPos, toPos, fromTarget, toTarget, 700);
  }

  function flyBack() {
    if (!originalCameraPosition) return;
    const currentTarget = controls?.target?.clone() ?? new THREE.Vector3();
    animateCamera(camera.position.clone(), originalCameraPosition.clone(), currentTarget, originalTarget ?? new THREE.Vector3(), 600);
  }

  function animateCamera(fromPos: THREE.Vector3, toPos: THREE.Vector3, fromTarget: THREE.Vector3, toTarget: THREE.Vector3, durationMs: number) {
    const start = performance.now();
    const animateStep = (t: number) => {
      const elapsed = t - start;
      const p = Math.min(1, elapsed / durationMs);
      const eased = easeInOutCubic(p);
      camera.position.lerpVectors(fromPos, toPos, eased);
      if (controls && controls.target) {
        const newTarget = new THREE.Vector3();
        newTarget.lerpVectors(fromTarget, toTarget, eased);
        controls.target.copy(newTarget);
      }
      if (p < 1) {
        requestAnimationFrame(animateStep);
      }
    };
    requestAnimationFrame(animateStep);
  }

  function easeInOutCubic(x: number) {
    return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
  }

  function resetCamera() {
    const maybeReset = (controls as any)?.reset;
    if (typeof maybeReset === 'function') {
      maybeReset.call(controls);
    } else {
      flyBack();
    }
  }
</script>

<div bind:this={container} class="three-container">
  <button class="camera-reset" on:click={resetCamera} aria-label="Reset camera">Reset camera</button>
</div>

<style>
  .three-container {
    width: 100vw;
    height: 100vh;
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
    opacity: 0;
    animation: fadeIn 400ms ease forwards;
  }

  .camera-reset {
    position: absolute;
    right: 12px;
    top: 12px;
    z-index: 100000; /* above CSS3DRenderer (z-index: 1) */
    background: rgba(0,0,0,0.7);
    color: #fff;
    border: 1px solid rgba(255,255,255,0.25);
    border-radius: 6px;
    padding: 6px 10px;
    cursor: pointer;
    font-size: 12px;
    backdrop-filter: blur(6px);
  }

  .camera-reset:hover {
    background: rgba(0,0,0,0.85);
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
</style>